import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import '../styles/CeoManager.css';

const getAdminToken = () => localStorage.getItem('adminKey') || '';

const api = axios.create({ baseURL: API_BASE_URL });
api.interceptors.request.use((config) => {
  config.headers['x-admin-token'] = getAdminToken();
  return config;
});

const CeoManager = () => {
  const [stocks, setStocks] = useState([]);
  const [allImages, setAllImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState(null);

  // Image picker modal
  const [pickerStock, setPickerStock] = useState(null);
  const [pickerSearch, setPickerSearch] = useState('');

  // Export modal
  const [exportCode, setExportCode] = useState(null);
  const [copied, setCopied] = useState(false);

  // Track which cards have unsaved text edits
  const [dirtyFields, setDirtyFields] = useState({});
  // Track local edits for name/description
  const [localEdits, setLocalEdits] = useState({});
  // Track saved feedback
  const [savedFeedback, setSavedFeedback] = useState({});

  const showMessage = useCallback((text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [stocksRes, imagesRes] = await Promise.all([
        api.get('/api/stocks'),
        api.get('/api/admin/ceo-images')
      ]);

      const stockList = Array.isArray(stocksRes.data) ? stocksRes.data :
                        stocksRes.data.stocks ? stocksRes.data.stocks : [];
      // Sort by stock ID number
      stockList.sort((a, b) => {
        const numA = parseInt(a.id.replace('stock-', ''));
        const numB = parseInt(b.id.replace('stock-', ''));
        return numA - numB;
      });
      setStocks(stockList);

      if (imagesRes.data.success) {
        setAllImages(imagesRes.data.images);
      }
    } catch (err) {
      showMessage('Failed to load data: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getLocalValue = (stockId, field) => {
    const key = `${stockId}-${field}`;
    if (localEdits[key] !== undefined) return localEdits[key];
    const stock = stocks.find(s => s.id === stockId);
    if (!stock) return '';
    return stock[field] || '';
  };

  const setLocalValue = (stockId, field, value) => {
    const key = `${stockId}-${field}`;
    setLocalEdits(prev => ({ ...prev, [key]: value }));
    setDirtyFields(prev => ({ ...prev, [key]: true }));
    setSavedFeedback(prev => ({ ...prev, [key]: false }));
  };

  const saveField = async (stockId, field) => {
    const key = `${stockId}-${field}`;
    const value = localEdits[key];
    if (value === undefined) return; // No local edit

    const body = {};
    if (field === 'ceo_name') body.ceoName = value;
    if (field === 'ceo_description') body.ceoDescription = value;

    try {
      const res = await api.put(`/api/admin/stock/${stockId}/ceo`, body);
      if (res.data.success) {
        setStocks(prev => prev.map(s => s.id === stockId ? { ...s, ...res.data.stock } : s));
        setDirtyFields(prev => ({ ...prev, [key]: false }));
        setSavedFeedback(prev => ({ ...prev, [key]: true }));
        setTimeout(() => setSavedFeedback(prev => ({ ...prev, [key]: false })), 2000);
      }
    } catch (err) {
      showMessage('Save failed: ' + err.message, 'error');
    }
  };

  const handleKeyDown = (e, stockId, field) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      saveField(stockId, field);
    }
  };

  const selectImage = async (stock, image) => {
    try {
      const imageUrl = image.url;
      const otherUsers = image.usedBy.filter(u => u.id !== stock.id);

      if (otherUsers.length > 0) {
        // Swap: give the other stock(s) our current photo
        const ourCurrentPhoto = stock.ceo_picture_url;
        for (const other of otherUsers) {
          await api.put(`/api/admin/stock/${other.id}/ceo`, { ceoPictureUrl: ourCurrentPhoto });
        }
        // Now assign the selected photo to us
        await api.put(`/api/admin/stock/${stock.id}/ceo`, { ceoPictureUrl: imageUrl });
        showMessage(`Swapped photos: ${stock.ticker} <-> ${otherUsers.map(u => u.ticker).join(', ')}`);
      } else {
        await api.put(`/api/admin/stock/${stock.id}/ceo`, { ceoPictureUrl: imageUrl });
        showMessage(`Photo updated for ${stock.ticker}`);
      }

      // Refresh both stocks and images
      const [stocksRes, imagesRes] = await Promise.all([
        api.get('/api/stocks'),
        api.get('/api/admin/ceo-images')
      ]);
      const stockList = Array.isArray(stocksRes.data) ? stocksRes.data :
                        stocksRes.data.stocks ? stocksRes.data.stocks : [];
      stockList.sort((a, b) => parseInt(a.id.replace('stock-', '')) - parseInt(b.id.replace('stock-', '')));
      setStocks(stockList);
      if (imagesRes.data.success) setAllImages(imagesRes.data.images);

      setPickerStock(null);
    } catch (err) {
      showMessage('Failed to update photo: ' + err.message, 'error');
    }
  };

  const handleExport = async () => {
    try {
      const res = await api.get('/api/admin/export-stock-seed');
      if (res.data.success) {
        setExportCode(res.data.seedCode);
        setCopied(false);
      }
    } catch (err) {
      showMessage('Export failed: ' + err.message, 'error');
    }
  };

  const copyExport = () => {
    navigator.clipboard.writeText(exportCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const filtered = stocks.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return s.ticker?.toLowerCase().includes(q) ||
           s.name?.toLowerCase().includes(q) ||
           s.ceo_name?.toLowerCase().includes(q);
  });

  const filteredImages = allImages.filter(img => {
    if (!pickerSearch) return true;
    return img.filename.toLowerCase().includes(pickerSearch.toLowerCase());
  });

  if (loading) {
    return <div className="ceo-manager"><div className="ceo-loading">Loading stocks...</div></div>;
  }

  return (
    <div className="ceo-manager">
      {message && <div className={`ceo-message ${message.type}`}>{message.text}</div>}

      <div className="ceo-manager-header">
        <h2>CEO Photo & Description Manager</h2>
        <div className="ceo-toolbar">
          <input
            className="ceo-search"
            type="text"
            placeholder="Search ticker, name, CEO..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <button className="ceo-export-btn" onClick={handleExport}>Export Seed Data</button>
        </div>
      </div>

      <div className="ceo-grid">
        {filtered.map(stock => (
          <div key={stock.id} className="ceo-card">
            <div className="ceo-card-photo" onClick={() => { setPickerStock(stock); setPickerSearch(''); }}>
              <img
                src={stock.ceo_picture_url || '/ceo/default.jpg'}
                alt={stock.ceo_name || stock.ticker}
                onError={e => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80"><rect fill="%23333" width="80" height="80"/><text x="40" y="45" text-anchor="middle" fill="%23666" font-size="12">No img</text></svg>'; }}
              />
              <div className="change-overlay">Change</div>
            </div>
            <div className="ceo-card-info">
              <div className="ceo-card-ticker">{stock.ticker}</div>
              <div className="ceo-card-stock-name">{stock.name}</div>

              <div className="ceo-card-field">
                <label>CEO Name</label>
                <input
                  value={getLocalValue(stock.id, 'ceo_name')}
                  onChange={e => setLocalValue(stock.id, 'ceo_name', e.target.value)}
                  onBlur={() => saveField(stock.id, 'ceo_name')}
                  onKeyDown={e => handleKeyDown(e, stock.id, 'ceo_name')}
                  placeholder="CEO Name"
                />
              </div>

              <div className="ceo-card-field">
                <label>Description</label>
                <textarea
                  value={getLocalValue(stock.id, 'ceo_description')}
                  onChange={e => setLocalValue(stock.id, 'ceo_description', e.target.value)}
                  onBlur={() => saveField(stock.id, 'ceo_description')}
                  onKeyDown={e => handleKeyDown(e, stock.id, 'ceo_description')}
                  placeholder="CEO Description"
                />
              </div>

              {dirtyFields[`${stock.id}-ceo_name`] || dirtyFields[`${stock.id}-ceo_description`] ? (
                <button className="ceo-card-save" onClick={() => {
                  saveField(stock.id, 'ceo_name');
                  saveField(stock.id, 'ceo_description');
                }}>Save</button>
              ) : (savedFeedback[`${stock.id}-ceo_name`] || savedFeedback[`${stock.id}-ceo_description`]) ? (
                <span className="ceo-card-saved">Saved</span>
              ) : null}
            </div>
          </div>
        ))}
      </div>

      {/* Image Picker Modal */}
      {pickerStock && (
        <div className="ceo-modal-overlay" onClick={() => setPickerStock(null)}>
          <div className="ceo-modal" onClick={e => e.stopPropagation()}>
            <div className="ceo-modal-header">
              <div className="ceo-modal-stock-info">
                <img
                  className="ceo-modal-current-photo"
                  src={pickerStock.ceo_picture_url || '/ceo/default.jpg'}
                  alt={pickerStock.ticker}
                  onError={e => { e.target.style.display = 'none'; }}
                />
                <div>
                  <h2>Select CEO Photo</h2>
                  <span className="ceo-modal-stock-label">{pickerStock.ticker} — {pickerStock.name}</span>
                </div>
              </div>
              <button className="ceo-modal-close" onClick={() => setPickerStock(null)}>×</button>
            </div>
            <div className="ceo-modal-search">
              <input
                type="text"
                placeholder="Filter images..."
                value={pickerSearch}
                onChange={e => setPickerSearch(e.target.value)}
                autoFocus
              />
            </div>
            <div className="ceo-modal-body">
              {filteredImages.map(img => {
                const isSelected = pickerStock.ceo_picture_url === img.url;
                const usedByOthers = img.usedBy.filter(u => u.id !== pickerStock.id);
                const isUsedByOthers = usedByOthers.length > 0;
                return (
                  <div
                    key={img.filename}
                    className={`ceo-image-option ${isSelected ? 'selected' : ''}`}
                    onClick={() => selectImage(pickerStock, img)}
                  >
                    <img src={img.url} alt={img.filename} loading="lazy" />
                    {isUsedByOthers && (
                      <div className="usage-badge">
                        {usedByOthers.map(u => u.ticker).join(', ')}
                      </div>
                    )}
                    {isUsedByOthers && <div className="swap-badge">SWAP</div>}
                    {!isUsedByOthers && !isSelected && <div className="unused-badge">FREE</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {exportCode !== null && (
        <div className="ceo-modal-overlay" onClick={() => setExportCode(null)}>
          <div className="ceo-modal ceo-export-modal" onClick={e => e.stopPropagation()}>
            <div className="ceo-modal-header">
              <h2>Export Seed Data</h2>
              <button className="ceo-modal-close" onClick={() => setExportCode(null)}>×</button>
            </div>
            <div className="ceo-modal-body">
              <p style={{ color: '#888', marginBottom: 12, fontSize: 13 }}>
                Copy this into both <code>scripts/seed-database.js</code> and <code>server/db.js</code>
              </p>
              <div className="ceo-export-code">{exportCode}</div>
              <button className={`ceo-copy-btn ${copied ? 'copied' : ''}`} onClick={copyExport}>
                {copied ? 'Copied!' : 'Copy to Clipboard'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CeoManager;
